import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../services/supabase'
import { useAuth } from './useAuth'
import type { SupplierMitigationTaskNote } from '../types'

/**
 * Load / upsert the current user's private note for a mitigation task.
 */
export function useMitigationTaskNote(taskId: string | null | undefined) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? null
  const enabled = Boolean(taskId && userId)

  const query = useQuery<SupplierMitigationTaskNote | null>({
    queryKey: ['supplier_mitigation_task_notes', taskId, userId],
    queryFn: async () => {
      if (!taskId || !userId) return null
      const { data, error } = await supabase
        .from('supplier_mitigation_task_notes')
        .select('*')
        .eq('task_id', taskId)
        .eq('user_id', userId)
        .maybeSingle()
      if (error) throw error
      return (data as SupplierMitigationTaskNote | null) ?? null
    },
    enabled,
    staleTime: 30_000,
  })

  const [draft, setDraft] = useState('')

  useEffect(() => {
    setDraft(query.data?.body ?? '')
  }, [query.data?.body, taskId])

  const saveMutation = useMutation({
    mutationFn: async (body: string) => {
      if (!taskId || !userId) throw new Error('Not signed in.')
      const { data, error } = await supabase
        .from('supplier_mitigation_task_notes')
        .upsert(
          {
            task_id: taskId,
            user_id: userId,
            body,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'task_id,user_id' }
        )
        .select()
        .single()
      if (error) throw error
      return data as SupplierMitigationTaskNote
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['supplier_mitigation_task_notes', taskId, userId],
      })
    },
  })

  return {
    body: draft,
    setBody: setDraft,
    loading: query.isLoading,
    saving: saveMutation.isPending,
    error: query.error || saveMutation.error,
    save: () => saveMutation.mutateAsync(draft),
    enabled,
  }
}
